/*
 * pz-design-embed.js
 * デザインシミュレーター（design.printeez.jp）の専用ページ・カート投入・デザインIDで開く・マイデザイン。
 * 設計: printeez-portal/docs/design-simulator/shopify-cart-integration.html 4章（App Proxy）・8章（テーマ、専用ページ方式）
 *
 * 使う場所
 * - 専用ページ /pages/design（sections/main-design-page.liquid + snippets/pz-design-embed.liquid）
 *     ?product=品番                → iframe で editor/new（新しく作る）
 *     ?id=公開ID（#key= / &v=）    → iframe で editor/open（保存したデザインを開く）
 *       #view=3d                  → 画像のカードが出る共有リンク（printeez.jp/apps/printeez/d/…）から。editor/open に渡し、
 *                                   アクリルキーホルダーは開いたらすぐ 3D のプレビュー（?view= は Shopify がテンプレートの
 *                                   指定に使うのでフラグメント。layout/theme.liquid が退避する）
 *     パラメータ無し                → シミュレーター対応アイテムの選択と「デザインIDで開く」
 * - 「デザインIDで開く」入力欄（snippets/pz-design-open-form.liquid）
 * - アカウントページのマイデザイン（sections/main-account.liquid）
 * - カートの行の差し替え（段階 4。same-print-product-switch.html 3.2）: エディタが「同じデザイン」で保存したとき、カートの
 *   このデザインの行をエディタに渡し（pz:cart-lines）、新しい内容の行に差し替える（pz:replace-cart → pz:replace-done）。
 *   カートのページの上のお知らせは assets/pz-cart-notice.js（snippets/pz-cart-notice.liquid）
 * - エディタのカート（段階 6。assets/pz-editor-cart.js）: エディタが pz:ready の features で "editor-cart" を知らせたら、
 *   エディタのカートの中身を pz:editor-cart で送る。カートに入れる・枚数を変える（pz:editor-cart-op）・差し替えるのはエディタの
 *   カートだけで、お店のカートには触らない。お店のカートに写すのは「注文に進む」（pz:checkout）で /cart へ移るときだけ。
 *   カートに入れた後は /cart へ移らずにエディタのカートを開かせる。別のデザインを開く（pz:open-design）も受ける。
 *   features の無い（古い）エディタ・受け口の無い（古い）portal・お客様を特定できないときは今のまま（カートに入れたら /cart へ）
 *
 * - 先行起動（専用ページの ?product= / ?id=）: このファイルは defer なのでページの残りの読み込みを待つ。それまでエディタの読み込みを
 *   待たせないよう、snippets/pz-design-embed.liquid（early_start）の小さなスクリプトが、同じ規則で iframe の URL を組んで先に iframe を作る。
 *   mount() は、URL が全く同じならその iframe を引き継ぎ（takeEarlyFrame）、先行起動のあいだに届いたメッセージを順に処理する。
 *   URL の組み方（パス・パラメータ・並び順）を変えるときは、そのスクリプトも同じに直す（食い違うと捨てて作り直す = 速くならないだけ）
 *   流入元（utm_source / utm_medium / utm_campaign。URL か cookie pz_utm_*）も iframe の URL の末尾に付ける（readUtm / appendUtm。先行起動のスクリプトと同じ規則）
 * - 読み込みの速さの計測（エディタの pz:timing）: エディタが「準備できた」（ready）・最初の操作（first_action）を知らせたら、
 *   ストアのページを開き始めてからの時間を GA4（sim_ready / sim_first_action）に送り、ready は Clarity にも（sim_ready_store_ms）。
 *   ready のときは window に pz:sim-ready も出す（layout/theme.liquid が、専用ページで遅らせている gtag.js・Clarity を読むきっかけ）。
 *
 * portal 側（エディタ・App Proxy）とメッセージ名・パスを合わせるときは、下の CONFIG だけを直す。
 */
(function () {
  'use strict';
  if (window.PzDesign) return;

  var CONFIG = {
    /* iframe の origin。postMessage の送信先（targetOrigin）と受信元の検査に使う（ワイルドカードは使わない）。
       Liquid の設定値（data-design-origin）で上書きできるが、https のオリジンだけ受け付ける */
    designOrigin: 'https://design.printeez.jp',
    /* design ホストのパス */
    paths: {
      editorNew: '/design/api/editor/new', /* ?product=品番&embed=1（新しく作る） */
      editorOpen: '/design/api/editor/open', /* ?id=公開ID&key=秘密鍵&v=版番号&embed=1&view=3d（デザインID・編集リンクで開く） */
      upload: '/design/upload/' /* {品番}?embed=1（完全入稿 = お客様の AI で注文。sections/main-upload-page.liquid） */
    },
    embedParam: 'embed',
    /* App Proxy（printeez.jp/apps/printeez/* → portal /api/proxy/*。Shopify が署名と logged_in_customer_id を付ける） */
    proxyBase: '/apps/printeez',
    proxy: {
      designCart: '/design-cart', /* POST: カートに入れる行を返す（設計 4.1〜4.5） */
      /* POST: カート全体の料金の行（製版代・洗浄料・サイズアップ）を決める（same-print-product-switch.html 4.6。assets/pz-cart-fees.js） */
      designCartFees: '/design-cart-fees',
      /* POST: エディタのカート（段階 6。assets/pz-editor-cart.js） */
      editorCart: '/editor-cart',
      designSession: '/design-session', /* GET: ログイン中の顧客トークン（設計 4.6） */
      myDesigns: '/my-designs' /* GET: マイデザイン一覧（設計 4.6） */
    },
    /* 専用ページ（設計 8.1）と、新しく作るときの品番のパラメータ名 */
    designPage: '/pages/design',
    productParam: 'product',
    /* 再注文ページ（sections/main-reorder.liquid。マイデザインの「再注文」の行き先。?id=公開ID&v=版番号） */
    reorderPage: '/pages/reorder',
    cartAddUrl: '/cart/add.js',
    cartUrl: '/cart',
    /* カートの行の差し替え（段階 4）: カートを読む・前の行を消す */
    cartJsUrl: '/cart.js',
    cartUpdateUrl: '/cart/update.js',
    /* カートのページの上のお知らせ（段階 4。assets/pz-cart-notice.js が読む localStorage のキー） */
    noticeStorageKey: 'pz:cart-notice',
    /* layout/theme.liquid が URL の id / key / v を退避する sessionStorage のキー */
    storageKey: 'pz:design-link',
    messages: {
      /* iframe → 親 */
      ready: 'pz:ready', /* 読込完了。{ publicId? } 親は顧客トークンを送る */
      saved: 'pz:saved', /* 保存した。{ publicId }（コピーとして保存したときは新しい公開 ID）*/
      addToCart: 'pz:add-to-cart', /* { payload: { action, publicId, versionNo?, editKey?, contentHash?, colorQuantities, options?, ... } } */
      sessionRefresh: 'pz:session-refresh', /* 顧客トークンの期限切れ。親が取り直して送り直す */
      resize: 'pz:resize', /* { height } */
      changeProduct: 'pz:change-product', /* { productCode } → アイテム選択へ（今と違う品番のときだけ ?product=品番 へ）。古いエディタだけが送る */
      productChanged: 'pz:product-changed', /* { productCode, productName } エディタで開いている商品（「商品を変更」で切り替えた）→ 上部の商品名を変える */
      timing: 'pz:timing', /* { phase: "ready" | "first_action", at } at = そのときの時刻（エポックミリ秒）。親が GA4・Clarity に送る。イベントごとに1回だけ */
      /* 親 → iframe */
      session: 'pz:session', /* { token, expiresAt } */
      cartError: 'pz:cart-error', /* { error: { ok:false, code, message, details?, status } } */
      /* カートの行の差し替え（段階 4。same-print-product-switch.html 3.2） */
      cartLines: 'pz:cart-lines', /* iframe → 親 { requestId, publicId } / 親 → iframe { requestId, ok, lines: [{ key, variantId, quantity, versionId, color }] } */
      replaceCart: 'pz:replace-cart', /* iframe → 親 { requestId, payload: { publicId, editKey, contentHash, embroiderySizeMm, decisions, idempotencyKey } } */
      replaceDone: 'pz:replace-done', /* 親 → iframe { requestId, result } */
      /* エディタのカート（段階 6）。pz:ready の features: string[] に "editor-cart" があるエディタだけが使う */
      editorCartOp: 'pz:editor-cart-op', /* iframe → 親 { requestId, op: "get" | "set_quantity" | "remove" | "quote", target?, pending? } */
      editorCart: 'pz:editor-cart', /* 親 → iframe { requestId?, reason: "sync" | "added" | "op", ok, view?, revision?, merged?, error?: { code, message } } */
      checkout: 'pz:checkout', /* iframe → 親 {}: お店のカートをそろえてから /cart へ */
      openDesign: 'pz:open-design' /* iframe → 親 { publicId }: 専用ページでそのデザインを開く */
    },
    /* pz:change-product で品番として読むフィールド（先頭が正式。エディタ側の実装と合わせる） */
    changeProductFields: ['productCode', 'product', 'code'],
    /* pz:cart-lines をエディタのカートから答えるとき、取り直しを待つ長さ（エディタは返事を 4 秒待つ。過ぎたら前に取った中身で答える） */
    cartLinesWaitMs: 2500,
    sessionRefreshBeforeMs: 10 * 60 * 1000, /* 顧客トークン（1 時間）の期限の 10 分前に取り直す */
    sessionFallbackIntervalMs: 50 * 60 * 1000,
    slowLoadMs: 20000,
    /* 商品の写真を出しているとき、iframe の load の後にエディタの「準備できた」を待つ長さ（load の直後はまだ白い画面のことがある。来なければ外す） */
    placeholderGraceMs: 2000,
    minFrameHeight: 480,
    maxFrameHeight: 4000
  };

  var PUBLIC_ID_RE = /^[0-9A-HJKMNP-TV-Z]{12}$/;
  var EDIT_KEY_RE = /^[A-Za-z0-9_-]{22}$/;
  var VERSION_RE = /^[1-9][0-9]{0,5}$/;
  var IDEMPOTENCY_RE = /^[A-Za-z0-9-]{8,64}$/;
  var PRODUCT_CODE_RE = /^[A-Z0-9][A-Z0-9_-]{1,39}$/;
  /* エディタのカート（段階 6）: エディタが pz:ready の features で知らせる名前と、受け口の操作 */
  var EDITOR_CART_FEATURE = 'editor-cart';
  var EDITOR_CART_OPS = ['get', 'set_quantity', 'remove', 'quote'];
  /*
   * デザインIDの入力で取り除く区切り文字（空白に加えて、Unicode の Pd（ハイフン類）とマイナス記号・長音記号）。
   * portal の design-ids.ts normalizePublicId と同じ扱い。ソースを ASCII に保つため文字コードで持つ
   */
  var SEPARATOR_CODES = [
    0x2d, 0x58a, 0x5be, 0x1400, 0x1806, 0x2010, 0x2011, 0x2012, 0x2013, 0x2014, 0x2015, 0x2212,
    0x2e17, 0x2e1a, 0x2e3a, 0x2e3b, 0x2e40, 0x301c, 0x3030, 0x30a0, 0x30fc, 0xfe31, 0xfe32, 0xfe58,
    0xfe63, 0xff0d
  ];

  var ERROR_MESSAGES = {
    unauthorized: 'ページを再読み込みしてから、もう一度お試しください。',
    forbidden: 'ログインしてからもう一度お試しください。',
    design_not_found: 'デザインIDが見つかりません。',
    design_expired: 'このデザインは保存期限を過ぎました。',
    version_conflict: '保存した内容が変わりました。もう一度保存してからカートに入れてください。',
    /* portal の文と同じ（お客様向けの文は短く。「行」「差し替え」「決済画面」は使わない） */
    in_progress: '前の操作を処理しています。少し待ってから、もう一度お試しください',
    rate_limited: 'しばらく時間をおいてから、もう一度お試しください',
    shopify_error: 'しばらく時間をおいてから、もう一度お試しください',
    internal_error: 'しばらく時間をおいてから、もう一度お試しください',
    not_available: '現在、シミュレーターからカートに入れる機能は準備中です。',
    bad_response: 'カートに入れられませんでした。時間をおいてもう一度お試しください。',
    cart_add_failed: 'カートに追加できませんでした。',
    network: '通信に失敗しました。電波の良いところでもう一度お試しください。'
  };

  /* ------------------------------------------------------------------ */
  /* 共通                                                                 */
  /* ------------------------------------------------------------------ */

  function log() {
    if (!window.console || !console.log) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[pz-design]');
    console.log.apply(console, args);
  }

  function warn() {
    if (!window.console || !console.warn) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[pz-design]');
    console.warn.apply(console, args);
  }

  /* https のオリジンだけ受け付ける（パス・ワイルドカード付きの値は捨てる） */
  function toOrigin(value) {
    if (!value) return null;
    try {
      var u = new URL(String(value).trim());
      if (u.protocol !== 'https:' || !u.hostname) return null;
      return u.origin;
    } catch (e) {
      return null;
    }
  }

  /*
   * 流入元（utm_source / utm_medium / utm_campaign）。エディタの iframe の URL に付ける（エディタが自分の URL から読む）。
   * snippets/pz-design-embed.liquid の先行起動と同じ規則（変えるときは両方。食い違うと先行起動の iframe を捨てて作り直す = 速くならないだけ）。
   * - このページの URL に utm があればそれ（3つ一組。cookie とは混ぜない）。無ければ cookie（pz_utm_source / _medium / _campaign。assets/pz-line-routing.js が保存）
   * - 値は小文字・[a-z0-9_.-] だけ・40 文字まで。値のあるものだけ付ける。ほかのクエリは持ち込まない
   */
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'];
  function cleanUtm(v) {
    return String(v == null ? '' : v).toLowerCase().replace(/[^a-z0-9_.-]/g, '').slice(0, 40);
  }
  function readUtm() {
    var out = {};
    try {
      var q = new URLSearchParams(window.location.search);
      var fromUrl = UTM_KEYS.some(function (k) { return !!cleanUtm(q.get(k)); });
      UTM_KEYS.forEach(function (k) {
        var v = '';
        if (fromUrl) {
          v = q.get(k);
        } else {
          var m = document.cookie.match(new RegExp('(?:^|;\\s*)pz_' + k + '=([^;]*)'));
          try { v = m ? decodeURIComponent(m[1]) : ''; } catch (e) { v = ''; }
        }
        v = cleanUtm(v);
        if (v) out[k] = v;
      });
    } catch (e) { /* 読めなければ付けない */ }
    return out;
  }
  function appendUtm(url) {
    var u = readUtm();
    UTM_KEYS.forEach(function (k) {
      if (u[k]) url.searchParams.set(k, u[k]);
    });
  }

  /* 「Canvaから」の旗。このページの URL に ?canva=1 があるときだけ、エディタの iframe の URL に canva=1 を付ける
     （審査が通るまで、旗のあるときだけエディタが Canva のタブを出す。snippets/pz-design-embed.liquid の appendCanvaFlag と同じ規則。変えるときは両方） */
  function appendCanvaFlag(url) {
    try {
      if (new URLSearchParams(window.location.search).get('canva') === '1') url.searchParams.set('canva', '1');
    } catch (e) { /* 読めなければ付けない */ }
  }

  /* 同一オリジン内のパスだけ受け付ける（//evil.example などを弾く） */
  function toLocalPath(value, fallback) {
    var s = String(value || '').trim();
    if (!s || s.charAt(0) !== '/' || s.charAt(1) === '/' || s.charAt(1) === '\\') return fallback;
    return s.replace(/\/+$/, '') || fallback;
  }

  function applySettings(node) {
    if (!node || !node.getAttribute) return;
    var origin = toOrigin(node.getAttribute('data-design-origin'));
    if (origin) CONFIG.designOrigin = origin;
    CONFIG.designPage = toLocalPath(node.getAttribute('data-design-page'), CONFIG.designPage);
    CONFIG.proxyBase = toLocalPath(node.getAttribute('data-proxy-base'), CONFIG.proxyBase);
    CONFIG.cartUrl = toLocalPath(node.getAttribute('data-cart-url'), CONFIG.cartUrl);
  }

  function proxyUrl(name) {
    return CONFIG.proxyBase + CONFIG.proxy[name];
  }

  /* エディタのカート（段階 6。assets/pz-editor-cart.js）。読み込まれていなければ null（今の動き） */
  function editorCart() {
    var ec = window.PzEditorCart;
    if (!ec || typeof ec.call !== 'function') return null;
    ec.config.endpoint = proxyUrl('editorCart');
    /* 合わせた後に料金の行を作り直す受け口（pz-cart-fees.js） */
    if (window.PzCartFees) window.PzCartFees.config.endpoint = proxyUrl('designCartFees');
    return ec;
  }

  function nfkc(value) {
    var s = String(value == null ? '' : value);
    try {
      s = s.normalize('NFKC');
    } catch (e) { /* normalize 非対応 */ }
    return s;
  }

  function stripSeparators(s) {
    var out = '';
    for (var i = 0; i < s.length; i++) {
      var ch = s.charAt(i);
      if (/\s/.test(ch) || SEPARATOR_CODES.indexOf(s.charCodeAt(i)) >= 0) continue;
      out += ch;
    }
    return out;
  }

  /*
   * 入力されたデザインID を DB の形（大文字・区切りなしの 12 文字）へ。
   * NFKC → 大文字化 → 先頭の # と空白・ハイフン類の除去 → Crockford の読み替え（O → 0、I・L → 1）。
   * portal の src/features/design-simulator/design-ids.ts normalizePublicId と同じ規則。
   */
  function cleanPublicId(input) {
    var s = nfkc(input).toUpperCase().replace(/^\s*#/, '');
    return stripSeparators(s).replace(/O/g, '0').replace(/[IL]/g, '1');
  }

  function normalizePublicId(input) {
    var s = cleanPublicId(input);
    return PUBLIC_ID_RE.test(s) ? s : null;
  }

  function formatPublicId(id) {
    var n = normalizePublicId(id);
    if (!n) return String(id || '');
    return n.slice(0, 4) + '-' + n.slice(4, 8) + '-' + n.slice(8, 12);
  }

  /* 品番（例 DF-1101）。NFKC → 前後の空白除去 → 大文字化。形が合わなければ null */
  function normalizeProductCode(input) {
    var s = nfkc(input).trim().toUpperCase();
    return PRODUCT_CODE_RE.test(s) ? s : null;
  }

  /*
   * 「デザインIDで開く」の入力を解釈する。デザインID / 編集リンク（URL）の貼り付けの両方を受ける。
   * 戻り値: { ok: true, id, key, v } / { ok: false, message }
   */
  function parseDesignInput(raw) {
    var text = String(raw == null ? '' : raw).trim();
    if (!text) return { ok: false, message: 'デザインIDを入力してください。' };

    if (/[?&#]id=/.test(text) || /^https?:\/\//i.test(text)) {
      var u = null;
      try {
        u = new URL(text, window.location.origin);
      } catch (e) {
        u = null;
      }
      /* 編集リンクは ?id=&key= の形と、#id=&key= / ?id=…#key=…（フラグメント）の形の両方を受ける */
      var hp = new URLSearchParams(u && u.hash && u.hash.indexOf('=') >= 0 ? u.hash.replace(/^#/, '') : '');
      var fromParam = function (name) {
        return (u && u.searchParams.get(name)) || hp.get(name) || '';
      };
      if (u && fromParam('id')) {
        var fromUrl = normalizePublicId(fromParam('id'));
        if (!fromUrl) return { ok: false, message: 'リンクのデザインIDが正しくありません。リンクをもう一度コピーしてお試しください。' };
        var k = fromParam('key');
        var vv = fromParam('v');
        return { ok: true, id: fromUrl, key: EDIT_KEY_RE.test(k) ? k : '', v: VERSION_RE.test(vv) ? vv : '' };
      }
      return { ok: false, message: 'リンクからデザインIDを読み取れませんでした。デザインID（例: 7K3M-2Q9X-PB4T）を入力してください。' };
    }

    var cleaned = cleanPublicId(text);
    if (cleaned.length !== 12) {
      return { ok: false, message: 'デザインIDは 12 文字です（例: 7K3M-2Q9X-PB4T）。' };
    }
    var id = normalizePublicId(text);
    if (!id) return { ok: false, message: 'デザインIDに使えない文字が含まれています。もう一度ご確認ください。' };
    return { ok: true, id: id, key: '', v: '' };
  }

  /*
   * 専用ページで保存したデザインを開く URL。onDesignPage なら今のページのパスのまま（他のパラメータは残す）。
   * 秘密鍵はフラグメント（#key=）に置く: クエリはサーバーに届き、Shopify が window.__st.pageurl（解析）に埋め込むため。
   * 専用ページ（layout/theme.liquid）は ?key= と #key= の両方を受ける。
   * view（共有リンクから来たときの 3D のプレビュー）は持ち越さない（別のデザインを開くときは、いつもの画面で開く）。
   */
  function designPageUrl(link, onDesignPage) {
    var base = onDesignPage ? window.location.pathname : CONFIG.designPage;
    var params = new URLSearchParams(onDesignPage ? window.location.search : '');
    ['id', 'key', 'v', 'view', CONFIG.productParam].forEach(function (n) {
      params.delete(n);
    });
    params.set('id', link.id);
    if (link.v) params.set('v', link.v);
    return base + '?' + params.toString() + (link.key ? '#key=' + encodeURIComponent(link.key) : '');
  }

  /* 専用ページで新しく作る URL（品番が無ければアイテム選択） */
  function productPageUrl(code) {
    var c = normalizeProductCode(code);
    return CONFIG.designPage + (c ? '?' + CONFIG.productParam + '=' + encodeURIComponent(c) : '');
  }

  /* layout/theme.liquid が URL から退避した { id, key, v, view } を読む（view はちょうど '3d' のときだけ残す） */
  function readDesignLink() {
    var link = null;
    try {
      link = JSON.parse(window.sessionStorage.getItem(CONFIG.storageKey) || 'null');
    } catch (e) {
      link = null;
    }
    if (!link && window.__pzDesignLink) link = window.__pzDesignLink;
    if (!link || !link.id) return null;
    var id = normalizePublicId(link.id);
    if (!id) return { invalid: true, raw: String(link.id).slice(0, 80) };
    return {
      id: id,
      key: EDIT_KEY_RE.test(link.key || '') ? link.key : '',
      v: VERSION_RE.test(String(link.v || '')) ? String(link.v) : '',
      view: link.view === '3d' ? '3d' : ''
    };
  }

  function writeDesignLink(link) {
    window.__pzDesignLink = link;
    try {
      window.sessionStorage.setItem(CONFIG.storageKey, JSON.stringify(link));
    } catch (e) { /* 保存できない環境では再読み込みで新規に戻るだけ */ }
  }

  function newIdempotencyKey() {
    var c = window.crypto || window.msCrypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    var bytes = new Uint8Array(16);
    if (c && c.getRandomValues) {
      c.getRandomValues(bytes);
    } else {
      for (var i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = Array.prototype.map.call(bytes, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
    return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
  }

  /* JSON 以外（App Proxy の先が 404 の HTML 等）も落とさずに読む */
  function readResponse(res) {
    return res.text().then(function (text) {
      var json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch (e) {
        json = null;
      }
      return {
        status: res.status,
        ok: res.ok,
        json: json && typeof json === 'object' ? json : null,
        retryAfter: res.headers ? res.headers.get('Retry-After') : null
      };
    });
  }

  function yen(n) {
    return '¥' + Number(n || 0).toLocaleString('ja-JP');
  }

  function formatDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.getFullYear() + '/' + ('0' + (d.getMonth() + 1)).slice(-2) + '/' + ('0' + d.getDate()).slice(-2);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* メッセージから最初に見つかった文字列のフィールド（payload の中も見る） */
  function pickField(data, names) {
    var sources = [data, data && data.payload];
    for (var s = 0; s < sources.length; s++) {
      var src = sources[s];
      if (!src || typeof src !== 'object') continue;
      for (var i = 0; i < names.length; i++) {
        var v = src[names[i]];
        if (typeof v === 'string' && v.trim()) return v;
      }
    }
    return '';
  }

  /* カートに入れる行の形を確かめる（Shopify /cart/add.js の items 形式） */
  function toCartLines(list) {
    if (!Array.isArray(list)) return null;
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var it = list[i] || {};
      var id = Number(it.id);
      var qty = Number(it.quantity);
      if (!(id > 0) || Math.floor(qty) !== qty || qty <= 0) return null;
      var props = {};
      if (it.properties && typeof it.properties === 'object') {
        Object.keys(it.properties).forEach(function (k) {
          var v = it.properties[k];
          if (v !== null && v !== undefined) props[k] = String(v);
        });
      }
      out.push({ id: id, quantity: qty, properties: props });
    }
    return out;
  }

  /* ------------------------------------------------------------------ */
  /* 顧客トークン（設計 4.6。ログイン中だけ。URL には載せず postMessage で渡す）            */
  /* ------------------------------------------------------------------ */

  var sessionCache = null; /* { token, expiresAt, expiresAtMs } */
  var sessionPromise = null;

  function fetchSession(force) {
    var now = Date.now();
    if (!force && sessionCache && sessionCache.expiresAtMs - now > CONFIG.sessionRefreshBeforeMs) {
      return Promise.resolve(sessionCache);
    }
    if (sessionPromise) return sessionPromise;
    sessionPromise = fetch(proxyUrl('designSession'), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(readResponse)
      .then(function (r) {
        if (!r.json || r.json.ok === false) {
          warn('design-session', r.status, r.json && r.json.code);
          return null;
        }
        if (!r.json.customerToken) return null;
        var ms = Date.parse(r.json.expiresAt || '');
        sessionCache = {
          token: String(r.json.customerToken),
          expiresAt: r.json.expiresAt || null,
          expiresAtMs: isNaN(ms) ? Date.now() + CONFIG.sessionFallbackIntervalMs + CONFIG.sessionRefreshBeforeMs : ms
        };
        return sessionCache;
      })
      .catch(function (e) {
        warn('design-session failed', e && e.message);
        return null;
      })
      .then(function (s) {
        sessionPromise = null;
        return s;
      });
    return sessionPromise;
  }

  /* ------------------------------------------------------------------ */
  /* 埋め込み（iframe と postMessage）                                     */
  /* ------------------------------------------------------------------ */

  var embeds = [];

  /* options: { productCode }（新しく作る）・{ link: { id, key, v, view } }（開く）・{ productCode, upload: true }（完全入稿のページ） */
  function DesignEmbed(root, options) {
    this.root = root;
    this.options = options || {};
    this.loggedIn = root.getAttribute('data-logged-in') === 'true';
    this.stage = root.querySelector('[data-pz-embed-stage]') || root;
    this.loadingEl = root.querySelector('[data-pz-embed-loading]');
    this.slowEl = root.querySelector('[data-pz-embed-slow]');
    this.phEl = root.querySelector('[data-pz-embed-ph]');
    this.busyEl = root.querySelector('[data-pz-embed-busy]');
    this.busyText = root.querySelector('[data-pz-embed-busy-text]');
    this.alertEl = root.querySelector('[data-pz-embed-alert]');
    this.alertText = root.querySelector('[data-pz-embed-alert-text]');
    this.unavailableEl = root.querySelector('[data-pz-embed-unavailable]');
    this.frame = null;
    this.src = null;
    this.ready = false;
    this.busy = false;
    this.sessionTimer = null;
    this.slowTimer = null;
    /* 読み込み中の商品の写真を出している間 true。load の後の待ち（placeholderGraceMs）のタイマー */
    this.phShown = false;
    this.loadTimer = null;
    /* 今エディタで開いている商品の品番（GA4 の product。pz:product-changed で変わる。開く画面は、分かればエディタの知らせから） */
    this.productCode = (options && options.productCode) || '';
    /* カートの行を差し替えている途中（段階 4） */
    this.replacing = false;
    /* エディタが pz:ready で知らせた機能（段階 6。"editor-cart" があればエディタのカートを使う） */
    this.features = [];
  }

  DesignEmbed.prototype.buildSrc = function () {
    var o = this.options;
    var url;
    if (o.upload && o.productCode) {
      url = new URL(CONFIG.paths.upload + encodeURIComponent(o.productCode), CONFIG.designOrigin);
    } else if (o.link && o.link.id) {
      url = new URL(CONFIG.paths.editorOpen, CONFIG.designOrigin);
      url.searchParams.set('id', o.link.id);
      if (o.link.key) url.searchParams.set('key', o.link.key);
      if (o.link.v) url.searchParams.set('v', o.link.v);
      /* 共有リンクから来たとき: 開いたらすぐ 3D のプレビュー（アクリルキーホルダー。ほかの商品のエディタは見ない） */
      if (o.link.view === '3d') url.searchParams.set('view', '3d');
    } else if (o.productCode) {
      url = new URL(CONFIG.paths.editorNew, CONFIG.designOrigin);
      url.searchParams.set('product', o.productCode);
      /* LP の「つくってみる」のデザイン（takeLpSeed）。中身の検査と置くのはエディタ（portal の lp-seed.ts） */
      if (o.seed) {
        Object.keys(o.seed).forEach(function (k) {
          url.searchParams.set(k, o.seed[k]);
        });
      }
    } else {
      return null;
    }
    url.searchParams.set(CONFIG.embedParam, '1');
    /* 流入元（utm）。先行起動の iframe の URL と同じ並び（embed の後ろ）で付ける */
    appendUtm(url);
    /* 「Canvaから」の旗（utm の後ろ。完全入稿のページには付けない） */
    if (!(o.upload && o.productCode)) appendCanvaFlag(url);
    return url.toString();
  };

  DesignEmbed.prototype.mount = function () {
    var self = this;
    this.src = this.buildSrc();
    if (!this.src) {
      if (this.loadingEl) this.loadingEl.hidden = true;
      if (this.unavailableEl) this.unavailableEl.hidden = false;
      this.stage.classList.add('pz-demb__stage--empty');
      warn('iframe の URL を組めません（品番・デザインIDなし）');
      return false;
    }
    this.updateOffset();
    /* snippets/pz-design-embed.liquid の先行起動で、もう読み込みを始めている iframe（同じ URL のものだけ引き継ぐ） */
    var early = this.takeEarlyFrame();
    /* 先行起動の間に、エディタがもう「準備できた」を知らせていたら、読み込み中の写真は出さない */
    var readyAlready = !!early && early.queue.some(function (m) { return m.type === CONFIG.messages.ready; });
    /* 写真の取得を iframe の読み込みと同時に始める */
    if (!readyAlready) this.showPlaceholder();
    var frame = early ? early.frame : document.createElement('iframe');
    if (!early) {
      frame.className = 'pz-demb__frame';
      frame.title = 'デザインシミュレーター';
      /* エディタはページの主役なので、遅らせず先に読む（loading は既定が eager だが、はっきり書く） */
      frame.setAttribute('loading', 'eager');
      frame.setAttribute('fetchpriority', 'high');
      frame.setAttribute('allow', 'clipboard-write; web-share');
      frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    }
    frame.addEventListener('load', function () {
      /* src を入れる前の about:blank の load は数えない */
      if (frame.getAttribute('src')) self.onFrameLoad();
    });
    this.frame = frame;
    if (early) {
      /* 幅と高さは、先行起動のときに確かめてある（iframe を作ったあとの src の入れ替えも要らない） */
      var earlyRect = frame.getBoundingClientRect();
      log('mounted (early)', Math.round(earlyRect.width) + 'x' + Math.round(earlyRect.height), this.src.replace(/([?&]key=)[^&]+/, '$1***'));
    } else {
      this.stage.appendChild(frame);
      this.whenSized(frame, function (rect) {
        frame.src = self.src;
        log('mounted', Math.round(rect.width) + 'x' + Math.round(rect.height), self.src.replace(/([?&]key=)[^&]+/, '$1***'));
      });
    }
    this.slowTimer = window.setTimeout(function () {
      if (!self.ready && self.slowEl) self.slowEl.hidden = false;
    }, CONFIG.slowLoadMs);
    if (this.loggedIn) {
      /* トークンの期限に関係なく、定期的に取り直して送り直す（設計 8.3） */
      window.setInterval(function () {
        self.sendSession(true);
      }, CONFIG.sessionFallbackIntervalMs);
    }
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        self.updateOffset();
      }, 150);
    });
    /*
     * ヘッダー・上部バーの高さが、画面の大きさと関係なく変わったとき（画像・書体の読み込み、お知らせの出し入れ）も合わせ直す。
     * エディタを出しているときはページを動かさない（layout/theme.liquid）ので、ずれた分の iframe の下端が見えなくならないように
     */
    if (window.ResizeObserver) {
      var targets = this.offsetTargets();
      var offsetObserver = new window.ResizeObserver(function () {
        self.updateOffset();
      });
      /* 余白の変化も拾うよう外枠（border-box）で見る */
      if (targets.header) offsetObserver.observe(targets.header, { box: 'border-box' });
      if (targets.bar) offsetObserver.observe(targets.bar, { box: 'border-box' });
      /* ヘッダーより上の帯（お知らせなど）の出し入れ・高さの変化も拾う */
      var above = document.querySelectorAll('.announcement-bar, .utility-bar');
      for (var ai = 0; ai < above.length; ai++) offsetObserver.observe(above[ai], { box: 'border-box' });
    }
    if (early) {
      /* 先行起動のあいだにエディタから届いていたメッセージ（pz:ready・pz:timing など）を、届いた順に処理する。
         タイマーなどの用意がすんだ後（pz:ready で読み込み中の画面を外すときに、20 秒のタイマーも止める） */
      early.queue.forEach(function (data) {
        try {
          self.onMessage(data);
        } catch (e) {
          warn('先行起動のメッセージを処理できませんでした', e && e.message);
        }
      });
      /* load がもう済んでいたなら、あとから足した load の聞き取りには届かないので、ここで同じ処理をする */
      if (early.loaded) this.onFrameLoad();
    }
    return true;
  };

  /*
   * 先行起動（snippets/pz-design-embed.liquid の early_start）で作られた iframe を引き継ぐ。
   * 引き継ぐのは、完全入稿のページ以外で、その iframe の URL がここで組んだ URL（this.src）と全く同じときだけ。
   * 食い違えば（URL の組み方がずれた等）捨てて、今までどおり作る。先行起動がためておいたメッセージはここで受け取る。
   * 戻り値: { frame, queue, loaded } / null
   */
  DesignEmbed.prototype.takeEarlyFrame = function () {
    if (this.options.upload) return null;
    var frame = this.stage.querySelector('iframe[data-pz-early]');
    if (!frame) return null;
    var early = frame.__pzEarly;
    if (early && typeof early.stop === 'function') early.stop();
    if (!early || !this.src || frame.getAttribute('src') !== this.src) {
      warn('先に作った iframe の URL が、こちらで組んだ URL と違うため作り直します');
      if (frame.parentNode) frame.parentNode.removeChild(frame);
      return null;
    }
    frame.removeAttribute('data-pz-early');
    frame.__pzEarly = null;
    return { frame: frame, queue: early.queue || [], loaded: !!early.loaded };
  };

  /*
   * iframe が幅と高さを持ってから fn を呼ぶ（src を入れる）。
   * 非表示（display:none）のラッパーの中で読み込み始めると、エディタが幅 0 の画面で初期化され、
   * あとで表示されても描き直さず真っ白になるため。表示されるまで待つ（ResizeObserver、無ければフレームごとに確認）。
   */
  DesignEmbed.prototype.whenSized = function (frame, fn) {
    var done = false;
    var ro = null;
    var started = Date.now();
    function measure() {
      var r = frame.getBoundingClientRect();
      return r.width > 0 && r.height > 0 ? r : null;
    }
    function fire(r) {
      if (done) return;
      done = true;
      if (ro) ro.disconnect();
      fn(r || frame.getBoundingClientRect());
    }
    var first = measure();
    if (first) {
      fire(first);
      return;
    }
    warn('iframe の大きさが 0 のため、表示されるまで読み込みを待ちます');
    if (window.ResizeObserver) {
      ro = new window.ResizeObserver(function () {
        var r = measure();
        if (r) fire(r);
      });
      ro.observe(frame);
      return;
    }
    (function poll() {
      if (done) return;
      var r = measure();
      if (r) {
        fire(r);
        return;
      }
      if (Date.now() - started > 30000) {
        fire(null);
        return;
      }
      window.requestAnimationFrame(poll);
    })();
  };

  /* iframe の高さ = 表示領域 - ヘッダー - 上部バー（CSS 側で 100dvh を基準にする。設計 8.3 モバイル） */
  /* iframe の上にあるもの（ヘッダー・上部バー）。高さを iframe の高さから引く */
  DesignEmbed.prototype.offsetTargets = function () {
    var header = document.querySelector('.section-header') || document.querySelector('sticky-header') || document.querySelector('header');
    var scope = (this.root.closest && this.root.closest('[data-pz-dpage-embed]')) || this.root;
    return { header: header, bar: scope.querySelector('.pz-demb__bar') };
  };

  DesignEmbed.prototype.updateOffset = function () {
    var t = this.offsetTargets();
    var header = t.header;
    var h = header ? Math.round(header.getBoundingClientRect().height) : 0;
    if (!(h >= 0 && h < 400)) h = 0;
    var bar = t.bar;
    var b = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    if (!(b >= 0 && b < 200)) b = 0;
    var offset = h + b;
    /*
     * ヘッダーの上にお知らせの帯（announcement-bar）などがあると、ヘッダーと上部バーの高さだけでは足りず、
     * iframe の下端が画面の外に出る（エディタの下の段が切れる。2026-10-14）。エディタを出しているときはページを
     * 動かさないので、エディタの枠の上端の位置（ページの先頭から）をそのまま使う。測れないときは今までの足し算
     */
    var box = this.stage || this.frame;
    if (box && box.getBoundingClientRect) {
      var top = Math.round(box.getBoundingClientRect().top + (window.pageYOffset || 0));
      if (top >= offset && top < 600) offset = top;
    }
    this.root.style.setProperty('--pz-embed-offset', offset + 'px');
  };

  DesignEmbed.prototype.onFrameLoad = function () {
    var self = this;
    if (this.phShown) {
      /* 写真を出している間は、エディタの「準備できた」を少し待つ（load の直後はまだ白い画面のことがある）。来なければ読み込み中の画面を外す */
      if (!this.loadTimer) {
        this.loadTimer = window.setTimeout(function () {
          self.loadTimer = null;
          self.hideLoading();
        }, CONFIG.placeholderGraceMs);
      }
    } else {
      this.hideLoading();
    }
    /* pz:ready が来ない（古いエディタ等）場合の保険。送り先オリジンが違えばブラウザが捨てる */
    this.sendSession(false);
  };

  DesignEmbed.prototype.hideLoading = function () {
    if (this.loadingEl) this.loadingEl.hidden = true;
    if (this.slowEl) this.slowEl.hidden = true;
    if (this.slowTimer) {
      window.clearTimeout(this.slowTimer);
      this.slowTimer = null;
    }
    if (this.loadTimer) {
      window.clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
    this.removePlaceholder();
  };

  /*
   * 読み込み中の画面に商品の写真を出す（新しく作るとき。options.photoUrl = 一覧のカードの写真）。
   * 写真の枠は読み込み中の画面（stage の中で position:absolute）の中にあり、大きさも先に決まっているので、写真が届いても、
   * iframe に替わってもページのレイアウトは動かない。写真が読めなかったときは枠ごと外す（文字だけの今までの読み込み中の画面）
   */
  DesignEmbed.prototype.showPlaceholder = function () {
    var url = this.options.photoUrl;
    if (!this.phEl || !url || this.phShown) return;
    var self = this;
    var img = document.createElement('img');
    img.alt = '';
    img.decoding = 'async';
    img.setAttribute('fetchpriority', 'high');
    img.addEventListener('load', function () {
      img.className = 'is-loaded';
    });
    img.addEventListener('error', function () {
      self.removePlaceholder();
    });
    img.src = url;
    this.phEl.appendChild(img);
    this.phEl.hidden = false;
    this.phShown = true;
  };

  DesignEmbed.prototype.removePlaceholder = function () {
    this.phShown = false;
    if (!this.phEl) return;
    this.phEl.hidden = true;
    this.phEl.textContent = '';
  };

  DesignEmbed.prototype.post = function (message) {
    if (!this.frame || !this.frame.contentWindow) return;
    try {
      this.frame.contentWindow.postMessage(message, CONFIG.designOrigin);
    } catch (e) {
      warn('postMessage failed', e && e.message);
    }
  };

  DesignEmbed.prototype.sendSession = function (force) {
    if (!this.loggedIn) return;
    var self = this;
    fetchSession(force).then(function (s) {
      if (!s) return;
      self.post({ type: CONFIG.messages.session, token: s.token, expiresAt: s.expiresAt });
      self.scheduleSession(s);
    });
  };

  DesignEmbed.prototype.scheduleSession = function (s) {
    var self = this;
    if (this.sessionTimer) window.clearTimeout(this.sessionTimer);
    var wait = s.expiresAtMs - Date.now() - CONFIG.sessionRefreshBeforeMs;
    if (!(wait > 60 * 1000)) wait = 60 * 1000;
    this.sessionTimer = window.setTimeout(function () {
      self.sendSession(true);
    }, Math.min(wait, CONFIG.sessionFallbackIntervalMs));
  };

  DesignEmbed.prototype.onMessage = function (data) {
    var M = CONFIG.messages;
    switch (data.type) {
      case M.ready:
        this.ready = true;
        this.hideLoading();
        this.sendSession(false);
        this.rememberDesign(data);
        this.readFeatures(data);
        return;
      case M.saved:
        this.rememberDesign(data);
        this.showSavedDesign(data);
        return;
      case M.sessionRefresh:
        this.sendSession(true);
        return;
      case M.resize:
        this.resize(data.height);
        return;
      case M.changeProduct:
        this.changeProduct(pickField(data, CONFIG.changeProductFields));
        return;
      case M.productChanged:
        this.showProduct(data);
        return;
      case M.timing:
        this.onTiming(data);
        return;
      case M.addToCart:
        this.addToCart(data.payload && typeof data.payload === 'object' ? data.payload : data);
        return;
      case M.cartLines:
        this.answerCartLines(data);
        return;
      case M.replaceCart:
        this.replaceCart(data);
        return;
      case M.editorCartOp:
        this.editorCartOp(data);
        return;
      case M.checkout:
        this.checkout();
        return;
      case M.openDesign:
        this.openDesign(data);
        return;
      default:
        return;
    }
  };

  /*
   * iframe が開いているデザインの公開 ID を、このタブの sessionStorage にだけ覚える（URL・解析には出さない）。
   * 再読み込みしたときに新しいデザインを作り直さず、同じデザインを editor/open で開き直すため。
   * 同じデザインなら退避済みの鍵・版番号は残す。iframe から鍵は受け取らない。
   */
  DesignEmbed.prototype.rememberDesign = function (data) {
    var id = normalizePublicId(pickField(data, ['publicId']));
    if (!id) return;
    var cur = readDesignLink();
    var same = cur && !cur.invalid && cur.id === id;
    writeDesignLink({ id: id, key: same ? cur.key : '', v: same ? cur.v : '', at: Date.now() });
  };

  /*
   * エディタの計測の知らせ（pz:timing { phase, at }）。at はエディタがそのとき測った時刻（エポックミリ秒）で、
   * ストアのページを開き始めた時刻（performance.timeOrigin）からの差を「ストアのページを開いてからの時間」として GA4 に送る。
   * - phase "ready" → イベント sim_ready。それ以外（"first_action"）→ sim_first_action。イベントごとにこのページを開いている間に1回だけ
   * - ready のときは Clarity のタグ sim_ready_store_ms（時間の幅）も付ける
   * - ready のときは読み込み中の画面を外し、window に pz:sim-ready を出す（layout/theme.liquid が、専用ページで遅らせている
   *   gtag.js・Clarity を読み始めるきっかけ。gtag() / clarity() は読み込み前でも dataLayer・clarity.q にたまるので、先に呼んでよい）
   * 受け取る相手の検査（origin と iframe の window）は onWindowMessage でしている
   */
  var timingSent = {};

  function navigationStartMs() {
    var perf = window.performance;
    if (perf && typeof perf.timeOrigin === 'number' && isFinite(perf.timeOrigin)) return perf.timeOrigin;
    if (perf && perf.timing && perf.timing.navigationStart > 0) return perf.timing.navigationStart;
    return null;
  }

  function simBucket(ms) {
    if (ms < 3000) return '<3s';
    if (ms < 6000) return '3-6s';
    if (ms < 10000) return '6-10s';
    if (ms < 20000) return '10-20s';
    if (ms < 40000) return '20-40s';
    return '>40s';
  }

  function sendGtag() {
    if (typeof window.gtag !== 'function') {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }
    window.gtag.apply(window, arguments);
  }

  DesignEmbed.prototype.onTiming = function (data) {
    var isReady = data.phase === 'ready';
    var name = isReady ? 'sim_ready' : 'sim_first_action';
    if (timingSent[name]) return;
    timingSent[name] = true;

    var at = Number(data.at);
    if (!isFinite(at) || at <= 0) at = Date.now();
    var start = navigationStartMs();
    var elapsed = start === null ? null : Math.round(Math.min(600000, Math.max(0, at - start)));

    if (elapsed !== null) {
      var params = { value: elapsed, sim_ms: elapsed };
      var product = normalizeProductCode(pickField(data, ['productCode'])) || this.productCode;
      if (product) params.product = product;
      try {
        sendGtag('event', name, params);
        if (isReady && typeof window.clarity === 'function') window.clarity('set', 'sim_ready_store_ms', simBucket(elapsed));
      } catch (e) {
        warn('timing: 計測の送信に失敗', e && e.message);
      }
    }

    if (isReady) {
      this.hideLoading();
      try {
        window.dispatchEvent(new CustomEvent('pz:sim-ready', { detail: { ms: elapsed } }));
      } catch (e) { /* CustomEvent 非対応。gtag.js・Clarity は操作か 8 秒で読まれる */ }
    }
  };

  /*
   * 保存したら、専用ページ上部の表示を保存したデザインに合わせる。
   * コピーとして保存すると公開 ID が変わり、新しく作ったデザインは最初の保存で ID が付くため
   * （「新しいデザイン: …」から「デザインID … を開いています」に切り替える）。
   */
  DesignEmbed.prototype.showSavedDesign = function (data) {
    var id = normalizePublicId(pickField(data, ['publicId']));
    var page = this.root.closest ? this.root.closest('[data-pz-design-page]') : null;
    if (!id || !page) return;
    var current = page.querySelector('[data-pz-dpage-current]');
    if (current) current.textContent = formatPublicId(id);
    page.setAttribute('data-mode', 'open');
    Array.prototype.forEach.call(page.querySelectorAll('[data-pz-dpage-when]'), function (node) {
      node.hidden = node.getAttribute('data-pz-dpage-when') !== 'open';
    });
  };

  /*
   * エディタで開いている商品が変わった（エディタの上の商品一覧で「商品を変更」した。portal の段階 3）: 上部の商品名を変える。
   * 名前はエディタが送る商品名（品番が入っていなければ品番を添える。アイテム選択の productLabel と同じ形）。文字として入れる
   */
  DesignEmbed.prototype.showProduct = function (data) {
    var code = normalizeProductCode(pickField(data, ['productCode']));
    if (!code) return;
    this.productCode = code;
    var page = this.root.closest ? this.root.closest('[data-pz-design-page]') : null;
    var el = page ? page.querySelector('[data-pz-dpage-product]') : null;
    if (!el) return;
    var name = String(pickField(data, ['productName']) || '').trim().slice(0, 200);
    el.textContent = !name ? code : name.toUpperCase().indexOf(code) >= 0 ? name : name + '（' + code + '）';
  };

  DesignEmbed.prototype.resize = function (height) {
    var h = Number(height);
    if (!this.frame || !isFinite(h) || h <= 0) return;
    h = Math.max(CONFIG.minFrameHeight, Math.min(CONFIG.maxFrameHeight, Math.round(h)));
    this.frame.style.height = h + 'px';
    this.stage.classList.add('pz-demb__stage--sized');
  };

  /*
   * 商品変更（設計 8.3）: アイテム選択（パラメータ無しの専用ページ）へ移る。
   * エディタは今開いている商品の品番を送ってくるので、?product=品番 へ移ると同じ商品のエディタに戻るだけになる。
   * 送られてきた品番が今の ?product= と違う（エディタの中で別の商品を選んだ）ときだけ、その商品で新しく作る。
   */
  DesignEmbed.prototype.changeProduct = function (code) {
    var next = code ? normalizeProductCode(code) : null;
    if (code && !next) warn('change-product: 品番が不正', String(code).slice(0, 40));
    var currentRaw = new URLSearchParams(window.location.search).get(CONFIG.productParam);
    var current = currentRaw ? normalizeProductCode(currentRaw) : null;
    var inOpenMode = !!(this.options.link && this.options.link.id);
    window.location.href = next && !inOpenMode && current && next !== current ? productPageUrl(next) : CONFIG.designPage;
  };

  DesignEmbed.prototype.setBusy = function (on, text) {
    this.busy = !!on;
    if (this.busyEl) this.busyEl.hidden = !on;
    if (this.busyText && text) this.busyText.textContent = text;
  };

  DesignEmbed.prototype.showAlert = function (message) {
    if (!this.alertEl) return;
    if (this.alertText) this.alertText.textContent = message;
    this.alertEl.hidden = false;
  };

  DesignEmbed.prototype.hideAlert = function () {
    if (this.alertEl) this.alertEl.hidden = true;
  };

  /*
   * カートに入れる（設計 2章 3〜11 / 8.3）。
   * iframe の申告（金額・版数など）は送らない。送るのは設計 4.1 の項目だけ。
   * App Proxy が返した行（設計行＋料金行）をそのまま /cart/add.js に入れて /cart へ
   * （エディタのカートに対応したエディタで portal がエディタのカートに入れたときは、お店のカートには入れずにエディタのカートを
   * 開かせる。段階 6 の showAdded）。
   */
  DesignEmbed.prototype.buildCartRequest = function (p) {
    var body = {
      action: p.action === 'reorder' ? 'reorder' : 'add',
      publicId: String(p.publicId || ''),
      colorQuantities: p.colorQuantities && typeof p.colorQuantities === 'object' ? p.colorQuantities : {},
      idempotencyKey: IDEMPOTENCY_RE.test(String(p.idempotencyKey || '')) ? String(p.idempotencyKey) : newIdempotencyKey(),
      /*
       * 料金の行はカート全体で作る（same-print-product-switch.html 8.3 段階 2a）: design-cart は製版代・洗浄料・サイズアップの行を
       * 返さず、カートに入れた後に design-cart-fees が版ID ごとにカート全体で数えた行を返す（pz-cart-fees.js）
       */
      feeMode: 'cart'
    };
    if (p.versionNo !== undefined && p.versionNo !== null && p.versionNo !== '') body.versionNo = Number(p.versionNo);
    if (p.editKey) body.editKey = String(p.editKey);
    if (p.contentHash) body.contentHash = String(p.contentHash);
    if (Array.isArray(p.options)) body.options = p.options;
    if (p.embroiderySizeMm !== undefined && p.embroiderySizeMm !== null && p.embroiderySizeMm !== '') {
      body.embroiderySizeMm = Number(p.embroiderySizeMm);
    }
    if (typeof p.whiteInkRequired === 'boolean') body.whiteInkRequired = p.whiteInkRequired;
    if (Array.isArray(p.acknowledgedWarnings)) body.acknowledgedWarnings = p.acknowledgedWarnings;
    return body;
  };

  DesignEmbed.prototype.addToCart = function (payload) {
    var self = this;
    if (this.busy) {
      log('add-to-cart: 処理中のため無視');
      return Promise.resolve({ ok: false, code: 'busy' });
    }
    var body = this.buildCartRequest(payload || {});
    if (!body.publicId) {
      this.fail({ code: 'bad_request', message: 'デザインを保存してからカートに入れてください。' });
      return Promise.resolve({ ok: false, code: 'bad_request' });
    }
    /* エディタのカート（段階 6）: 入れた行を、この端末（ログイン中はお客様）のエディタのカートにも入れてもらう */
    var ec = editorCart();
    body.deviceKey = ec ? ec.deviceKey() : null;
    /*
     * エディタのカートに対応したエディタ（pz:ready の features）で、受け口が無いと分かっていなければ、portal がエディタのカートに
     * 入れたとき（応答の editorCart）はお店のカートに入れない（段階 6 の 0.1: お店のカートに写すのは「注文に進む」のときだけ）
     */
    var editorMode = !!ec && this.supportsEditorCart() && ec.available() !== false;
    this.hideAlert();
    this.setBusy(true, 'カートに追加しています…');
    log('add-to-cart', body.action, formatPublicId(body.publicId));

    /* カートに入れて料金の行をそろえるまで（エディタのカートの同期とは重ねずに順に行う） */
    var add = function () {
      return fetch(proxyUrl('designCart'), {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(readResponse, function (e) {
          throw { code: 'network', message: ERROR_MESSAGES.network, detail: e && e.message };
        })
        .then(function (r) {
          var j = r.json;
          if (!j) {
            /* App Proxy の先に受け口が無い（portal 未デプロイ等）と HTML の 404 が返る */
            throw { code: r.status === 404 ? 'not_available' : 'bad_response', status: r.status };
          }
          if (j.ok !== true) {
            throw {
              code: j.code === 'not_found' ? 'not_available' : j.code || 'bad_response',
              message: j.code === 'not_found' ? null : j.message,
              details: j.details,
              status: r.status,
              retryAfter: r.retryAfter
            };
          }
          var lines = toCartLines(j.items);
          var fees = j.feeItems === undefined ? [] : toCartLines(j.feeItems);
          if (!lines || !lines.length || !fees) throw { code: 'bad_response', status: r.status };
          var added = { lines: lines, fees: fees, response: j, editor: false };
          if (editorMode && j.editorCart && typeof j.editorCart === 'object') {
            added.editor = true;
            log('added to editor cart', lines.length, 'lines', j.summary ? yen(j.summary.subtotal) : '');
            return added;
          }
          return self.addLinesToStore(added).then(function () {
            return added;
          });
        });
    };

    return (ec ? ec.exclusive(add) : add())
      .then(function (added) {
        return added.editor ? self.showAdded(ec, added) : self.toCart();
      })
      .catch(function (err) {
        var e = err && typeof err === 'object' && err.code ? err : { code: 'bad_response', detail: String(err) };
        self.fail(e);
        return { ok: false, code: e.code };
      });
  };

  /* design-cart が返した行をお店のカートに入れて、カート全体の料金の行をそろえる（今の流れ。入れられなければ投げる） */
  DesignEmbed.prototype.addLinesToStore = function (added) {
    var self = this;
    var lines = added.lines;
    var fees = added.fees;
    var j = added.response;
    return fetch(CONFIG.cartAddUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines.concat(fees) })
    })
      .then(readResponse, function (e) {
        throw { code: 'network', message: ERROR_MESSAGES.network, detail: e && e.message };
      })
      .then(function (c) {
        if (!c.ok) {
          var d = c.json || {};
          throw {
            code: 'cart_add_failed',
            message: ERROR_MESSAGES.cart_add_failed + (d.description ? '（' + d.description + '）' : ''),
            status: c.status
          };
        }
        log('added', lines.length, 'lines +', fees.length, 'fee lines', j.summary ? yen(j.summary.subtotal) : '');
        return self.syncCartFees(j);
      });
  };

  DesignEmbed.prototype.toCart = function () {
    this.setBusy(true, 'カートに移動しています…');
    window.location.href = CONFIG.cartUrl;
    return { ok: true };
  };

  /*
   * エディタのカートに入れた後（段階 6）: get で中身を取って pz:editor-cart（reason "added"）を送る（エディタがカートの画面を開く。
   * /cart へは移らない・お店のカートには入れない）。取れないときは今のまま、お店のカートにも入れて /cart へ
   * （エディタのカートには入っているので、どちらにもある）
   */
  DesignEmbed.prototype.showAdded = function (ec, added) {
    var self = this;
    return ec.call('get').then(function (r) {
      if (r.ok && r.identified) {
        self.setBusy(false);
        self.postEditorCart('added', r);
        return { ok: true };
      }
      return ec
        .exclusive(function () {
          return self.addLinesToStore(added);
        })
        .then(function () {
          return self.toCart();
        });
    });
  };

  /*
   * カートに入れた後、カート全体の料金の行（製版代・洗浄料・サイズアップ）をそろえてから /cart へ移る（段階 2a）。
   * design-cart が feeMode "cart" を受けたとき（新しい portal）だけ。失敗しても /cart へ移る（カートのページがもう一度そろえる）
   */
  DesignEmbed.prototype.syncCartFees = function (response) {
    var fees = window.PzCartFees;
    if (!response || response.feeMode !== 'cart' || !fees) return Promise.resolve(null);
    fees.config.endpoint = proxyUrl('designCartFees');
    this.setBusy(true, 'カートの料金を計算しています…');
    return fees.sync({ force: true }).then(null, function () { return null; });
  };

  DesignEmbed.prototype.fail = function (e) {
    var message = e.message || ERROR_MESSAGES[e.code] || ERROR_MESSAGES.bad_response;
    if (e.code === 'rate_limited' && e.retryAfter) message += '（約' + e.retryAfter + '秒後）';
    warn('add-to-cart failed', e.code, e.status || '', e.detail || '');
    this.setBusy(false);
    this.showAlert(message);
    var error = { ok: false, code: e.code, message: message, status: e.status || null };
    if (e.details !== undefined) error.details = e.details;
    if (e.retryAfter) error.retryAfter = e.retryAfter;
    this.post({ type: CONFIG.messages.cartError, error: error });
  };

  /* ------------------------------------------------------------------ */
  /* カートの行の差し替え（段階 4。same-print-product-switch.html 3.2・3.3・8.5）       */
  /* ------------------------------------------------------------------ */

  /* 版の ID（エディタのカートの商品の versionId。昔の行は隠し項目 _pz_design_version）の形 */
  var VERSION_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(readResponse);
  }

  function getCart() {
    return fetch(CONFIG.cartJsUrl, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(readResponse)
      .then(function (r) {
        if (!r.ok || !r.json) throw new Error('cart.js ' + r.status);
        return r.json;
      });
  }

  function wait(ms) {
    return new Promise(function (resolve) { window.setTimeout(resolve, ms); });
  }

  /* 版の商品の handle（pz-design-<公開ID>-v<版番号>。衝突したときは末尾に -1 などが付く）から公開ID（大文字 12 文字）。違えば空文字 */
  function publicIdOfHandle(handle) {
    var m = /^pz-design-([0-9a-z]{12})-v\d+/i.exec(String(handle || ''));
    return m ? m[1].toUpperCase() : '';
  }

  /*
   * カートのこのデザインの行（版の商品 = 商品のタイプが PZ Design Product で、公開ID が同じ行。公開IDは商品の handle から、昔の行は
   * 隠し項目 _pz_design_pid からも）。秘密鍵（_pz_design_key）は渡さない。料金の行（_pz_fee_set）・オプションの行は版の商品では
   * ないので入らない。新しい行（2026-10-29 から隠し項目が無い）には版の ID が分からない: 昔の行の項目に版があれば付け、
   * 無ければ付けない（portal が行のバリアントから引く）。カラーはバリアントのオプション（variant_options[0]。昔の行は _pz_color）
   */
  function designLinesOf(cart, publicId) {
    var out = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      var p = it.properties && typeof it.properties === 'object' ? it.properties : {};
      var version = String(p._pz_design_version || '');
      if (it.product_type !== 'PZ Design Product' && !VERSION_ID_RE.test(version)) continue;
      var pid = normalizePublicId(p._pz_design_pid) || normalizePublicId(publicIdOfHandle(it.handle));
      if (pid !== publicId) continue;
      var variantId = Number(it.variant_id);
      var qty = Number(it.quantity);
      if (!(variantId > 0) || !(qty > 0)) continue;
      var color = typeof p._pz_color === 'string' ? p._pz_color : Array.isArray(it.variant_options) && typeof it.variant_options[0] === 'string' ? it.variant_options[0] : null;
      var line = {
        key: String(it.key),
        variantId: variantId,
        quantity: qty,
        color: color
      };
      if (VERSION_ID_RE.test(version)) line.versionId = version.toLowerCase();
      out.push(line);
    }
    return out;
  }

  /*
   * エディタのカートのこのデザインの商品（段階 6）: view.items のうち公開 ID が同じ商品の、サイズごとの
   * { key: エディタのカートの商品の id（sizes[].key）, variantId, quantity, versionId, color }（designLinesOf と同じ形）
   */
  function editorCartLinesOf(view, publicId) {
    var out = [];
    var items = view && Array.isArray(view.items) ? view.items : [];
    items.forEach(function (it) {
      if (!it || normalizePublicId(it.publicId) !== publicId) return;
      var version = String(it.versionId || '');
      if (!VERSION_ID_RE.test(version)) return;
      (Array.isArray(it.sizes) ? it.sizes : []).forEach(function (s) {
        var variantId = Number(s && s.variantId);
        var qty = Number(s && s.quantity);
        if (!s || typeof s.key !== 'string' || !s.key || !(variantId > 0) || !(qty > 0)) return;
        out.push({
          key: s.key,
          variantId: variantId,
          quantity: qty,
          versionId: version.toLowerCase(),
          color: typeof it.color === 'string' ? it.color : null
        });
      });
    });
    return out;
  }

  /* エディタのカートの合計（get の view.summary.total。分からなければ null） */
  function editorCartTotalOf(view) {
    var total = view && view.summary ? view.summary.total : null;
    return typeof total === 'number' && isFinite(total) ? total : null;
  }

  /*
   * カートの行の差し替え・pz:cart-lines をエディタのカートでするか（段階 6）: エディタのカートに対応したエディタで、
   * エディタのカートを使えると get で確かめ済み（受け口があり、お客様を特定できた）。それ以外は今のまま、お店のカートで
   */
  DesignEmbed.prototype.editorCartReady = function (ec) {
    return !!ec && this.supportsEditorCart() && ec.available() === true && ec.identified() === true;
  };

  /*
   * エディタに、カートのこのデザインの行を渡す（pz:cart-lines。保存の画面のプレビュー）。
   * エディタのカートを使えるときは、エディタのカートのこのデザインの商品で答える（お店のカートには触らない。段階 6）。
   * 取り直しが間に合わなければ、前に取った中身で答える
   */
  DesignEmbed.prototype.answerCartLines = function (data) {
    var self = this;
    var M = CONFIG.messages;
    var requestId = typeof data.requestId === 'string' ? data.requestId.slice(0, 80) : '';
    if (!requestId) return;
    var publicId = normalizePublicId(pickField(data, ['publicId']));
    if (!publicId) {
      this.post({ type: M.cartLines, requestId: requestId, ok: false });
      return;
    }
    var ec = editorCart();
    if (this.editorCartReady(ec)) {
      var cached = function () {
        var last = ec.lastResponse();
        return last && last.view ? last.view : null;
      };
      Promise.race([
        ec.call('get').then(function (r) {
          return r.ok && r.identified ? r.response.view : cached();
        }),
        wait(CONFIG.cartLinesWaitMs).then(cached)
      ]).then(function (view) {
        if (!view) {
          self.post({ type: M.cartLines, requestId: requestId, ok: false });
          return;
        }
        self.post({ type: M.cartLines, requestId: requestId, ok: true, lines: editorCartLinesOf(view, publicId) });
      });
      return;
    }
    getCart().then(
      function (cart) {
        self.post({ type: M.cartLines, requestId: requestId, ok: true, lines: designLinesOf(cart, publicId) });
      },
      function (e) {
        warn('cart-lines failed', e && e.message);
        self.post({ type: M.cartLines, requestId: requestId, ok: false });
      }
    );
  };

  /*
   * App Proxy の design-cart（action "replace"）に頼む。通信の失敗・5xx・429・処理中（409 in_progress）は待ってからやり直す
   * （同じ冪等キーなので二重に作らない。最大 3 回）
   */
  function askReplace(body) {
    var attempt = function (n) {
      return postJson(proxyUrl('designCart'), body).then(
        function (r) {
          var j = r.json;
          if (j && j.ok === true) return r;
          var retry = n < 2 && (r.status >= 500 || r.status === 429 || (r.status === 409 && j && j.code === 'in_progress'));
          if (!retry) return r;
          var after = Number(r.retryAfter) || 2;
          return wait(Math.min(5, Math.max(1, after)) * 1000).then(function () { return attempt(n + 1); });
        },
        function (e) {
          if (n >= 2) throw e;
          return wait(1500).then(function () { return attempt(n + 1); });
        }
      );
    };
    return attempt(0);
  }

  /* カートのページの上のお知らせを残す（assets/pz-cart-notice.js が出す。消すまで・2 時間まで） */
  function rememberCartNotice(notice) {
    try {
      window.localStorage.setItem(CONFIG.noticeStorageKey, JSON.stringify(notice));
    } catch (e) { /* 残せない環境ではお知らせを出さないだけ（エディタには結果を出す） */ }
    if (window.PzCartNotice && typeof window.PzCartNotice.render === 'function') window.PzCartNotice.render();
  }

  /*
   * カートの行の差し替え（pz:replace-cart。エディタが「同じデザイン」で保存した後に頼む）。
   * カートを読む → App Proxy の design-cart（action "replace"）→ 返ってきた新しい行を /cart/add.js で足す → 足せたら前の行を
   * /cart/update.js で消す（足せなければ消さない = 途中で失敗しても行が多めに残る側）→ カート全体の料金の行を作り直す
   * （2a の PzCartFees.sync）→ カートの合計をくらべて金額が変わったかを決める → お知らせを残してエディタへ結果（pz:replace-done）。
   * エディタのカートだけを差し替えるときは、差し替えの前と後のエディタのカートの合計（get の view.summary.total）をくらべる
   */
  DesignEmbed.prototype.replaceCart = function (data) {
    var self = this;
    var M = CONFIG.messages;
    var requestId = typeof data.requestId === 'string' ? data.requestId.slice(0, 80) : '';
    var p = data.payload && typeof data.payload === 'object' ? data.payload : {};
    var publicId = normalizePublicId(p.publicId);
    var finish = function (result) {
      self.replacing = false;
      self.post({ type: M.replaceDone, requestId: requestId, result: result });
    };
    var failed = function (message) {
      return {
        ok: false,
        replacedCount: 0,
        priceChanged: false,
        checkoutStarted: false,
        failed: [],
        removed: [],
        removeFailed: 0,
        feesFailed: false,
        message: message
      };
    };
    if (!requestId) return;
    if (!publicId || !EDIT_KEY_RE.test(String(p.editKey || '')) || !IDEMPOTENCY_RE.test(String(p.idempotencyKey || ''))) {
      this.post({ type: M.replaceDone, requestId: requestId, result: failed(ERROR_MESSAGES.unauthorized) });
      return;
    }
    if (this.replacing) {
      this.post({ type: M.replaceDone, requestId: requestId, result: failed(ERROR_MESSAGES.in_progress) });
      return;
    }
    this.replacing = true;
    var decisions = Array.isArray(p.decisions) ? p.decisions : [];
    /* エディタが選んだ扱い（行の key）。プレビューの後で入った行は、同じ版・同じカラーの行の扱い、無ければ差し替える */
    var actionFor = function (line) {
      var byKey = null;
      var byVersion = null;
      for (var i = 0; i < decisions.length; i++) {
        var d = decisions[i] || {};
        if (d.action !== 'replace' && d.action !== 'remove' && d.action !== 'keep') continue;
        if (d.key === line.key) byKey = d.action;
        else if (!byVersion && String(d.versionId || '').toLowerCase() === String(line.versionId || '') && d.color === line.color) byVersion = d.action;
      }
      return byKey || byVersion || 'replace';
    };
    var beforeTotal = null;
    var res = null;
    var removeFailed = 0;
    var feesOk = false;
    /* 差し替えた後に取り直したエディタのカート（合計をくらべるのに使い、そのまま pz:editor-cart（sync）でエディタへ送る） */
    var afterGet = null;
    var ec = editorCart();
    /*
     * エディタのカートだけを差し替えるか（段階 6 の 0.1: お店のカートには「注文に進む」まで触らない）: 差し替える行はエディタのカートの
     * このデザインの商品（pz:cart-lines と同じ）。portal がその商品を新しい版へ移す。それ以外は今のまま、お店のカートの行で差し替える
     */
    var editorMode = this.editorCartReady(ec);

    /* 差し替え（エディタのカートの同期とは重ねずに順に行う。direct = 順番待ちの中から受け口を呼ぶ） */
    var run = function (direct) {
      var linesOf = editorMode
        ? direct.call('get').then(function (r) {
            if (!r.ok || !r.identified) throw { message: r.message || ERROR_MESSAGES.internal_error };
            beforeTotal = editorCartTotalOf(r.response.view);
            return editorCartLinesOf(r.response.view, publicId);
          })
        : getCart().then(function (cart) {
            beforeTotal = typeof cart.total_price === 'number' ? cart.total_price : null;
            return designLinesOf(cart, publicId);
          });
      return linesOf
        .then(function (lines) {
          if (!lines.length) throw { message: 'カートにこのデザインの商品が見つかりませんでした' };
          var body = {
            action: 'replace',
            publicId: publicId,
            editKey: String(p.editKey),
            lines: lines.map(function (l) {
              return { key: l.key, variantId: l.variantId, quantity: l.quantity, versionId: l.versionId, action: actionFor(l) };
            }),
            idempotencyKey: String(p.idempotencyKey),
            feeMode: 'cart',
            /* エディタのカート（段階 6）: 差し替えた行に合わせて、エディタのカートの商品も新しい版へ移してもらう */
            deviceKey: ec ? ec.deviceKey() : null
          };
          if (/^[0-9a-f]{64}$/.test(String(p.contentHash || ''))) body.contentHash = String(p.contentHash);
          if ([80, 160, 240].indexOf(Number(p.embroiderySizeMm)) >= 0) body.embroiderySizeMm = Number(p.embroiderySizeMm);
          log('replace-cart', formatPublicId(publicId), lines.length, 'lines');
          return askReplace(body).then(null, function (e) {
            throw { message: ERROR_MESSAGES.network, detail: e && e.message };
          });
        })
        .then(function (r) {
          var j = r.json;
          if (!j) throw { message: r.status === 404 ? ERROR_MESSAGES.not_available : ERROR_MESSAGES.bad_response };
          if (j.ok !== true) throw { message: j.message || ERROR_MESSAGES[j.code] || ERROR_MESSAGES.bad_response };
          res = j;
          /* エディタのカートだけを差し替えた（portal が移した）: ここから下のお店のカートの差し替え・料金の行の作り直しはしない */
          if (editorMode) return null;
          var add = toCartLines(j.items || []);
          if (!add) throw { message: ERROR_MESSAGES.bad_response };
          if (!add.length) return null;
          return postJson(CONFIG.cartAddUrl, { items: add }).then(function (c) {
            if (!c.ok) {
              var d = c.json || {};
              /* 足せなければ前の行は消さない（前の内容のまま残る） */
              throw { message: 'カートに新しいデザインの商品を入れられませんでした' + (d.description ? '（' + d.description + '）' : '') + '。カートは前のデザインのままです' };
            }
            return null;
          });
        })
        .then(function () {
          if (editorMode) return null;
          var remove = Array.isArray(res.remove) ? res.remove.filter(function (k) { return typeof k === 'string' && k; }) : [];
          if (!remove.length) return null;
          var updates = {};
          remove.forEach(function (k) { updates[k] = 0; });
          return postJson(CONFIG.cartUpdateUrl, { updates: updates }).then(
            function (u) {
              if (!u.ok) {
                removeFailed = remove.length;
                warn('前の行を消せません', u.status);
              }
            },
            function () {
              removeFailed = remove.length;
            }
          );
        })
        .then(function () {
          /* カート全体の料金の行（製版代・洗浄料・サイズアップ）を作り直す（2a。失敗しても reject しない） */
          var fees = window.PzCartFees;
          if (editorMode || !fees) return null;
          fees.config.endpoint = proxyUrl('designCartFees');
          return fees.sync({ force: true }).then(function (r) {
            feesOk = !!(r && r.ok);
          });
        })
        .then(function () {
          /* 差し替えた後の合計（取り直せなければ null。差し替えは済んでいるので失敗にはしない） */
          if (editorMode) {
            return direct.call('get').then(
              function (r) {
                if (!r.ok || !r.identified) return null;
                afterGet = r;
                return editorCartTotalOf(r.response.view);
              },
              function () { return null; }
            );
          }
          return getCart().then(
            function (after) { return typeof after.total_price === 'number' ? after.total_price : null; },
            function () { return null; }
          );
        })
        .then(function (afterTotal) {
          /* 前と後の合計をくらべられれば、違うときだけ「金額が変わっています」。くらべられなければ portal の答え（段階 4 と同じ） */
          var priceChanged =
            (editorMode || feesOk) && beforeTotal !== null && afterTotal !== null
              ? afterTotal !== beforeTotal
              : !!(res.priceChanged || res.platesChanged);
          var pick = function (x) {
            return { productName: String((x && x.productName) || ''), color: String((x && x.color) || '') };
          };
          var result = {
            ok: true,
            replacedCount: Array.isArray(res.replaced) ? res.replaced.length : 0,
            priceChanged: priceChanged,
            checkoutStarted: !!res.checkoutStarted,
            failed: (Array.isArray(res.failed) ? res.failed : []).map(function (x) {
              var v = pick(x);
              v.message = String((x && x.message) || '');
              return v;
            }),
            removed: (Array.isArray(res.removed) ? res.removed : []).map(pick),
            removeFailed: removeFailed,
            feesFailed: !editorMode && !feesOk
          };
          /* カートを変えた（差し替えた・外した・差し替えられなかった）ときだけ、カートのページにお知らせを出す */
          var touched = result.replacedCount > 0 || result.removed.length > 0 || result.failed.length > 0 || removeFailed > 0;
          if (touched) {
            rememberCartNotice({
              v: 1,
              at: Date.now(),
              displayId: res.design && res.design.displayId ? String(res.design.displayId) : formatPublicId(publicId),
              replacedCount: result.replacedCount,
              priceChanged: result.priceChanged,
              checkoutStarted: result.checkoutStarted,
              failed: result.failed,
              removed: result.removed,
              removeFailed: result.removeFailed
            });
          }
          log('replaced', result.replacedCount, 'items', priceChanged ? 'price changed' : 'same price', removeFailed ? 'remove failed' : '');
          finish(result);
        })
        .catch(function (err) {
          var message = err && err.message ? String(err.message) : ERROR_MESSAGES.bad_response;
          warn('replace-cart failed', message, err && err.detail ? err.detail : '');
          finish(failed(message));
        });
    };

    (ec ? ec.exclusive(run) : run()).then(function () {
      /* 差し替えた後のエディタのカートを送る（段階 6。エディタのカートに対応したエディタだけ。合計をくらべるのに取り直した中身があればそれを） */
      if (!self.supportsEditorCart()) return;
      if (afterGet) self.postEditorCart('sync', afterGet);
      else self.sendEditorCart('sync');
    });
  };

  /* ------------------------------------------------------------------ */
  /* エディタのカート（段階 6。assets/pz-editor-cart.js）                     */
  /* ------------------------------------------------------------------ */

  /* エディタが pz:ready で知らせる機能。"editor-cart" があれば、エディタのカートを送る（無い = 古いエディタは今のまま） */
  DesignEmbed.prototype.readFeatures = function (data) {
    var list = Array.isArray(data.features) ? data.features : [];
    this.features = list.filter(function (f) { return typeof f === 'string'; }).slice(0, 20);
    if (this.supportsEditorCart()) this.sendEditorCart('sync');
  };

  DesignEmbed.prototype.supportsEditorCart = function () {
    return this.features.indexOf(EDITOR_CART_FEATURE) >= 0;
  };

  /* エディタへカートの中身を送る（pz:editor-cart）。r は PzEditorCart の結果 */
  DesignEmbed.prototype.postEditorCart = function (reason, r, requestId) {
    var msg = { type: CONFIG.messages.editorCart, reason: reason, ok: !!(r && r.ok && r.response) };
    if (requestId) msg.requestId = requestId;
    if (msg.ok) {
      msg.view = r.response.view;
      msg.revision = r.response.revision;
      msg.merged = r.response.merged || null;
    } else {
      msg.error = { code: String((r && r.code) || 'bad_response'), message: String((r && r.message) || ERROR_MESSAGES.internal_error) };
    }
    this.post(msg);
  };

  /*
   * エディタのカートの中身を取って（get）、エディタへ pz:editor-cart を送る。お店のカートには触らない（0.1。そろえるのは「注文に進む」）。
   * 受け口が無い（古い portal）・お客様を特定できないときは送らない（エディタはカートを出さず今の流れのまま）
   */
  DesignEmbed.prototype.sendEditorCart = function (reason) {
    var self = this;
    var ec = editorCart();
    if (!ec || ec.available() === false) return Promise.resolve(null);
    return ec.call('get').then(function (r) {
      if (!r.available || (r.ok && !r.identified)) return r;
      self.postEditorCart(reason, r);
      return r;
    });
  };

  /*
   * エディタのカートの操作（pz:editor-cart-op）。受け口に頼んで、pz:editor-cart（reason "op"・同じ requestId）で返す。
   * お店のカートには触らない（0.1。受け口の合わせ方は当てない）。quote（カートに入れた後の見積）は pending を渡すだけ
   */
  DesignEmbed.prototype.editorCartOp = function (data) {
    var self = this;
    var requestId = typeof data.requestId === 'string' ? data.requestId.slice(0, 80) : '';
    if (!requestId) return;
    var op = typeof data.op === 'string' ? data.op : '';
    var ec = editorCart();
    if (!ec || EDITOR_CART_OPS.indexOf(op) < 0) {
      this.postEditorCart('op', { ok: false, code: 'bad_request', message: ERROR_MESSAGES.unauthorized }, requestId);
      return;
    }
    var extra = {};
    if (data.target && typeof data.target === 'object') extra.target = data.target;
    if (op === 'quote' && data.pending && typeof data.pending === 'object') extra.pending = data.pending;
    ec.call(op, extra).then(function (r) {
      self.postEditorCart('op', r, requestId);
    });
  };

  /* 「注文に進む」（pz:checkout）: お店のカートをエディタのカートとそろえてから /cart へ（失敗しても移る。カートのページがもう一度そろえる） */
  DesignEmbed.prototype.checkout = function () {
    var ec = editorCart();
    var go = function () {
      window.location.href = CONFIG.cartUrl;
    };
    this.setBusy(true, 'カートに移動しています…');
    if (!ec || ec.available() === false) {
      go();
      return;
    }
    ec.sync().then(go, go);
  };

  /*
   * このデザインの秘密鍵（カートに入れたときに編集権があったときだけ。無ければ空文字）。昔の行はお店のカートの行の項目の鍵、
   * 新しい行（2026-10-29 から鍵を行に入れない）はエディタのカートの応答の鍵（editKeyFor。このページで受け口を呼んだことがあるとき）
   */
  function editKeyInCart(cart, publicId) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var p = items[i] && items[i].properties && typeof items[i].properties === 'object' ? items[i].properties : {};
      var key = String(p._pz_design_key || '');
      if (normalizePublicId(p._pz_design_pid) === publicId && EDIT_KEY_RE.test(key)) return key;
    }
    var ec = editorCart();
    var fromEditor = ec && typeof ec.editKeyFor === 'function' ? String(ec.editKeyFor(publicId) || '') : '';
    return EDIT_KEY_RE.test(fromEditor) ? fromEditor : '';
  }

  /*
   * 別のデザインを開く（pz:open-design { publicId }）: 専用ページ /pages/design?id=公開ID へ（プレビューの preview_theme_id は残す）。
   * お店のカートにそのデザインの秘密鍵があれば、カートのページの「デザインを編集」と同じく #key= を付ける。
   * カートをそろえている途中なら、終わってから移る（途中で移るとお店のカートの書き換えが止まる）
   */
  DesignEmbed.prototype.openDesign = function (data) {
    var id = normalizePublicId(pickField(data, ['publicId']));
    if (!id) {
      warn('open-design: デザインIDが不正');
      return;
    }
    var ec = editorCart();
    var read = function () {
      return getCart().then(
        function (cart) { return editKeyInCart(cart, id); },
        function () { return ''; }
      );
    };
    (ec ? ec.exclusive(read) : read()).then(function (key) {
      var params = new URLSearchParams();
      params.set('id', id);
      var preview = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (preview) params.set('preview_theme_id', preview);
      window.location.href = CONFIG.designPage + '?' + params.toString() + (key ? '#key=' + encodeURIComponent(key) : '');
    });
  };

  function onWindowMessage(ev) {
    if (ev.origin !== CONFIG.designOrigin) return;
    var data = ev.data;
    if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;
    for (var i = 0; i < embeds.length; i++) {
      var inst = embeds[i];
      if (inst.frame && ev.source === inst.frame.contentWindow) {
        inst.onMessage(data);
        return;
      }
    }
  }

  function initEmbed(root, options) {
    if (root.__pzEmbed) return root.__pzEmbed;
    applySettings(root);
    var inst = new DesignEmbed(root, options);
    root.__pzEmbed = inst;
    embeds.push(inst);
    var closeBtn = root.querySelector('[data-pz-embed-alert-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        inst.hideAlert();
      });
    }
    var reloadBtn = root.querySelector('[data-pz-embed-reload]');
    if (reloadBtn) {
      reloadBtn.addEventListener('click', function () {
        window.location.reload();
      });
    }
    inst.mount();
    return inst;
  }

  /* ------------------------------------------------------------------ */
  /* 専用ページ（/pages/design。設計 8.1 / 8.4）                             */
  /* ------------------------------------------------------------------ */

  function productLabel(root, code) {
    var cards = root.querySelectorAll('[data-pz-product-code]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].getAttribute('data-pz-product-code') === code) {
        var name = cards[i].getAttribute('data-pz-product-name') || '';
        if (!name) return code;
        /* 商品名に品番が入っていれば（例「… (DF-1101)」）重ねて出さない */
        return name.toUpperCase().indexOf(code) >= 0 ? name : name + '（' + code + '）';
      }
    }
    return code;
  }

  /*
   * 一覧のカード（snippets/pz-design-item-card.liquid）に出ている商品の写真の URL。読み込み中の画面に使う（新しいデータは持たない）。
   * シミュレーターのカードの写真（.pz-scard__img）か、無ければ Shopify の商品画像。カードが無い品番・https でない値は空文字
   */
  function productPhotoUrl(root, code) {
    var cards = root.querySelectorAll('[data-pz-product-code]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].getAttribute('data-pz-product-code') !== code) continue;
      var img = cards[i].querySelector('img.pz-scard__img, img.pz-dpage__card-img');
      var src = img ? img.getAttribute('src') || '' : '';
      return /^https:\/\//.test(src) ? src : '';
    }
    return '';
  }

  function initDesignPage(root) {
    applySettings(root);
    var intro = root.querySelector('[data-pz-dpage-intro]');
    var embedWrap = root.querySelector('[data-pz-dpage-embed]');
    var notice = root.querySelector('[data-pz-dpage-notice]');
    var link = readDesignLink();
    var rawProduct = new URLSearchParams(window.location.search).get(CONFIG.productParam);
    var product = rawProduct ? normalizeProductCode(rawProduct) : null;
    var mode = link && !link.invalid ? 'open' : product ? 'new' : 'select';
    root.setAttribute('data-mode', mode);

    if (mode === 'select' || !embedWrap) {
      document.documentElement.classList.remove('pz-design-link');
      if (intro) intro.hidden = false;
      if (embedWrap) embedWrap.hidden = true;
      if (link && link.invalid) {
        var form = root.querySelector('[data-pz-dpage-intro] [data-pz-design-open]');
        if (form) {
          var input = form.querySelector('input[name="id"]');
          if (input) input.value = link.raw;
          showFormError(form, 'デザインIDの形式が正しくありません。もう一度ご確認ください。');
        } else if (notice) {
          /* アイテム選択に「デザインIDで開く」欄が無い形（デザインIDは検索欄に入れる。assets/pz-design-select.js） */
          notice.textContent = 'デザインIDの形式が正しくありません。検索欄にデザインIDを入れ直してください。';
          notice.hidden = false;
        }
      } else if (rawProduct && !product && notice) {
        notice.textContent = '品番の形式が正しくありません。下のアイテムから選び直してください。';
        notice.hidden = false;
      }
      return;
    }

    /*
     * エディタの画面。html.pz-design-link で、スマホは上部バーと iframe を画面全体に重ね、ヘッダー・フッターを出さない
     * （layout/theme.liquid の head / pz-design-embed.css）。ふつうは head のスクリプトが付け済み。アイテム選択では上で外す
     */
    document.documentElement.classList.add('pz-design-link');
    if (intro) intro.hidden = true;
    embedWrap.hidden = false;
    Array.prototype.forEach.call(root.querySelectorAll('[data-pz-dpage-when]'), function (node) {
      node.hidden = node.getAttribute('data-pz-dpage-when') !== mode;
    });
    var current = root.querySelector('[data-pz-dpage-current]');
    var productEl = root.querySelector('[data-pz-dpage-product]');
    if (mode === 'open' && current) current.textContent = formatPublicId(link.id) + (link.v ? '（v' + link.v + '）' : '');
    if (mode === 'new' && productEl) productEl.textContent = productLabel(root, product);

    var toggle = root.querySelector('[data-pz-dpage-toggle]');
    var another = root.querySelector('[data-pz-dpage-another]');
    if (toggle && another) {
      var setAnother = function (open) {
        another.hidden = !open;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      };
      toggle.addEventListener('click', function () {
        var open = another.hidden;
        setAnother(open);
        /* PC では入力欄にすぐ入力できるように。スマホは押し間違いでキーボードが出ないよう、欄を押してから */
        var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
        if (open && !coarse) {
          var input = another.querySelector('input');
          if (input) input.focus();
        }
      });
      var closeBtn = another.querySelector('[data-pz-dpage-another-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          setAnother(false);
          toggle.focus();
        });
      }
      /* 欄の外を押したとき・Esc・エディタ（iframe）を触ったとき（親のウィンドウからフォーカスが外れる）に閉じる */
      document.addEventListener('pointerdown', function (e) {
        if (another.hidden) return;
        if (another.contains(e.target) || toggle.contains(e.target)) return;
        setAnother(false);
      });
      document.addEventListener('keydown', function (e) {
        if (!another.hidden && (e.key === 'Escape' || e.key === 'Esc')) setAnother(false);
      });
      window.addEventListener('blur', function () {
        if (!another.hidden && document.activeElement && document.activeElement.tagName === 'IFRAME') setAnother(false);
      });
    }

    var embedRoot = embedWrap.querySelector('[data-pz-design-embed]');
    if (embedRoot) {
      initEmbed(
        embedRoot,
        mode === 'open' ? { link: link } : { productCode: product, seed: takeLpSeed(), photoUrl: productPhotoUrl(root, product) }
      );
    }
  }

  /*
   * LP（/pages/b2b-for-teams-lp）の「つくってみる」から来たとき（?product=&seed=lp1&lines=&ink=&color=）、その項目を取り出して
   * このページの URL からは消す（読み込み直しで、同じデザインをもう一度置かないように）。エディタの editor/new にそのまま渡す。
   */
  var LP_SEED_PARAMS = ['seed', 'name', 'lines', 'ink', 'color'];
  function takeLpSeed() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('seed') !== 'lp1') return null;
    var seed = {};
    LP_SEED_PARAMS.forEach(function (k) {
      var v = params.get(k);
      if (v) seed[k] = v.slice(0, 200);
      params.delete(k);
    });
    try {
      var qs = params.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    } catch (e) {
      /* URL を書き換えられなくても、エディタには渡す */
    }
    return seed;
  }

  /* ------------------------------------------------------------------ */
  /* 「デザインIDで開く」入力欄（設計 8.4）                                    */
  /* ------------------------------------------------------------------ */

  function showFormError(form, message) {
    var err = form.querySelector('[data-pz-design-open-error]');
    var input = form.querySelector('input[name="id"]');
    if (err) {
      err.textContent = message || '';
      err.hidden = !message;
    }
    if (input) {
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }
  }

  function initOpenForm(form) {
    if (form.__pzOpen) return;
    form.__pzOpen = true;
    applySettings(form);
    var onDesignPage = form.getAttribute('data-target') === 'self';
    var input = form.querySelector('input[name="id"]');
    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      var parsed = parseDesignInput(input ? input.value : '');
      if (!parsed.ok) {
        showFormError(form, parsed.message);
        if (input) input.focus();
        return;
      }
      showFormError(form, '');
      if (input) input.value = formatPublicId(parsed.id);
      window.location.href = designPageUrl(parsed, onDesignPage);
    });
    if (input) {
      input.addEventListener('input', function () {
        showFormError(form, '');
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* マイデザイン（アカウントページ。設計 8.4）                                */
  /* ------------------------------------------------------------------ */

  function MyDesigns(root) {
    this.root = root;
    this.list = root.querySelector('[data-pz-mydes-list]');
    this.status = root.querySelector('[data-pz-mydes-status]');
    this.retry = root.querySelector('[data-pz-mydes-retry]');
    this.more = root.querySelector('[data-pz-mydes-more]');
    /* data-limit: この件数まで出し、多ければ「すべて表示」（専用ページ）。無ければ全部出す（アカウントページ） */
    var limit = parseInt(root.getAttribute('data-limit') || '', 10);
    this.limit = limit > 0 ? limit : 0;
    this.showAll = false;
    this.items = [];
    this.loading = false;
    this.loaded = false;
    var self = this;
    if (this.retry) {
      this.retry.addEventListener('click', function () {
        self.load(true);
      });
    }
    if (this.more) {
      this.more.addEventListener('click', function () {
        self.showAll = true;
        self.render(self.items);
      });
    }
  }

  MyDesigns.prototype.setStatus = function (text, canRetry) {
    if (this.status) {
      this.status.textContent = text || '';
      this.status.hidden = !text;
    }
    if (this.retry) this.retry.hidden = !canRetry;
  };

  MyDesigns.prototype.load = function (force) {
    var self = this;
    if (this.loading || (this.loaded && !force)) return;
    this.loading = true;
    this.setStatus('マイデザインを読み込んでいます…', false);
    fetch(proxyUrl('myDesigns'), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(readResponse)
      .then(function (r) {
        var j = r.json;
        if (!j || j.ok === false) {
          var code = j && j.code;
          if (r.status === 403 || code === 'forbidden') return self.setStatus('ログインするとマイデザインが表示されます。', false);
          if (r.status === 429 || code === 'rate_limited') return self.setStatus(ERROR_MESSAGES.rate_limited, true);
          if (!j || r.status === 404 || code === 'not_found') return self.setStatus('マイデザインは準備中です。もうしばらくお待ちください。', false);
          return self.setStatus('マイデザインを読み込めませんでした。時間をおいてもう一度お試しください。', true);
        }
        var items = Array.isArray(j.designs) ? j.designs : Array.isArray(j.items) ? j.items : Array.isArray(j) ? j : [];
        self.loaded = true;
        self.render(items);
      })
      .catch(function () {
        self.setStatus('マイデザインを読み込めませんでした。通信状況をご確認のうえ、もう一度お試しください。', true);
      })
      .then(function () {
        self.loading = false;
      });
  };

  MyDesigns.prototype.render = function (items) {
    var list = this.list;
    if (!list) return;
    list.innerHTML = '';
    var valid = items.filter(function (d) {
      return d && normalizePublicId(d.publicId || d.displayId);
    });
    this.items = items;
    var hiddenCount = 0;
    if (this.limit && !this.showAll && valid.length > this.limit) {
      hiddenCount = valid.length - this.limit;
      valid = valid.slice(0, this.limit);
    }
    if (this.more) {
      this.more.hidden = hiddenCount === 0;
      this.more.textContent = hiddenCount ? 'すべて表示（ほか' + hiddenCount + '件）' : '';
    }
    if (!valid.length) {
      this.setStatus('まだマイデザインはありません。ログイン中にシミュレーターで保存したデザインや、カートに入れたデザインがここに並びます。', false);
      return;
    }
    this.setStatus('', false);
    valid.forEach(function (d) {
      var id = normalizePublicId(d.publicId || d.displayId);
      var row = el('li', 'pz-mydes__item');

      var thumb = el('div', 'pz-mydes__thumb');
      var thumbUrl = String(d.thumbnailUrl || '');
      if (/^https:\/\//.test(thumbUrl)) {
        var img = document.createElement('img');
        img.src = thumbUrl;
        img.alt = '';
        img.width = 72;
        img.height = 72;
        img.loading = 'lazy';
        thumb.appendChild(img);
      } else {
        thumb.appendChild(el('span', 'pz-mydes__thumb-empty', '画像なし'));
      }
      row.appendChild(thumb);

      var body = el('div', 'pz-mydes__body');
      var head = el('div', 'pz-mydes__head');
      head.appendChild(el('span', 'pz-mydes__label', 'デザインID'));
      head.appendChild(el('span', 'pz-mydes__id', formatPublicId(id)));
      /* コピー・色違いは元のデザインID を添える（2026-10-02 オーナーの決定。portal の my-designs が sourceDisplayId を返す） */
      var srcId = d.sourceDisplayId ? normalizePublicId(String(d.sourceDisplayId)) : null;
      if (srcId) head.appendChild(el('span', 'pz-mydes__src', '（元 ' + formatPublicId(srcId) + '）'));
      var editable = d.access === 'edit';
      head.appendChild(el('span', 'pz-mydes__badge' + (editable ? ' pz-mydes__badge--edit' : ''), editable ? '編集可' : '閲覧のみ'));
      body.appendChild(head);
      body.appendChild(el('div', 'pz-mydes__name', d.name ? String(d.name) : '名前なし'));
      var meta = [];
      if (d.productCode) meta.push('品番 ' + String(d.productCode));
      if (d.updatedAt) meta.push('更新 ' + formatDate(d.updatedAt));
      meta.push(d.lastOrderedAt ? '最終注文 ' + formatDate(d.lastOrderedAt) : '未注文');
      body.appendChild(el('div', 'pz-mydes__meta', meta.join(' / ')));
      if (!editable) body.appendChild(el('div', 'pz-mydes__note', '直して保存すると、新しいデザインIDで保存されます（オリジナルは変わりません）。'));
      row.appendChild(body);

      var actions = el('div', 'pz-mydes__actions');
      var open = el('a', 'pz-mydes__btn pz-mydes__btn--primary', '開く');
      open.href = designPageUrl({ id: id }, false);
      actions.appendChild(open);
      var vNo = Number(d.lastOrderedVersionNo);
      if (vNo > 0 && Math.floor(vNo) === vNo) {
        /* 前回注文した版のまま、カラー・サイズ・枚数を選んでカートに入れる再注文ページへ（エディタは開かない） */
        var again = el('a', 'pz-mydes__btn', '再注文');
        again.href = CONFIG.reorderPage + '?id=' + encodeURIComponent(id) + '&v=' + vNo;
        again.title = '前回注文した内容（v' + vNo + '）で再注文します';
        actions.appendChild(again);
      }
      row.appendChild(actions);
      list.appendChild(row);
    });
  };

  /*
   * マイデザインの一覧を読む（専用ページのアイテム選択 assets/pz-design-select.js が使う）。
   * { ok, status, items } を返す。失敗（ログインしていない・通信の失敗など）は ok: false で、例外は投げない
   */
  function fetchMyDesigns() {
    return fetch(proxyUrl('myDesigns'), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(readResponse)
      .then(function (r) {
        var j = r.json;
        if (!j || j.ok === false) return { ok: false, status: r.status, items: [] };
        var items = Array.isArray(j.designs) ? j.designs : Array.isArray(j.items) ? j.items : Array.isArray(j) ? j : [];
        return { ok: true, status: r.status, items: items };
      })
      .catch(function () {
        return { ok: false, status: 0, items: [] };
      });
  }

  function initMyDesigns(root) {
    if (root.__pzMyDesigns) return root.__pzMyDesigns;
    applySettings(root);
    var inst = new MyDesigns(root);
    root.__pzMyDesigns = inst;
    return inst;
  }

  /* ------------------------------------------------------------------ */
  /* 起動                                                                 */
  /* ------------------------------------------------------------------ */

  window.addEventListener('message', onWindowMessage);

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pz-design-page]'), initDesignPage);
    Array.prototype.forEach.call(document.querySelectorAll('[data-pz-design-open]'), initOpenForm);
    Array.prototype.forEach.call(document.querySelectorAll('[data-pz-my-designs][data-autoload="true"]'), function (r) {
      /* 専用ページでデザインを開いている（アイテム選択を隠している）ときは読まない */
      var intro = r.closest ? r.closest('[data-pz-dpage-intro]') : null;
      if (intro && intro.hidden) return;
      initMyDesigns(r).load(false);
    });
  }

  window.PzDesign = {
    config: CONFIG,
    embeds: embeds,
    normalizePublicId: normalizePublicId,
    formatPublicId: formatPublicId,
    normalizeProductCode: normalizeProductCode,
    parseDesignInput: parseDesignInput,
    readDesignLink: readDesignLink,
    productPageUrl: productPageUrl,
    designPageUrl: designPageUrl,
    fetchMyDesigns: fetchMyDesigns,
    formatDate: formatDate,
    initEmbed: initEmbed,
    initOpenForm: initOpenForm,
    initMyDesigns: initMyDesigns
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
